import lottieWeb, { AnimationConfigWithData, AnimationItem } from 'lottie-web';
import { CSSProperties, type JSX, useEffect, useRef, useState } from 'react';
import LottieController, { createLottieController } from './LottieController';

type LottieProps = {
  // animationData is a string to ensure we don't run into this memory issue: https://github.com/airbnb/lottie-web/issues/2070
  animationData: string;
  className?: string;
  onLoad?: (lottieController: LottieController) => void;
  style?: CSSProperties;
  testId?: string;
} & Pick<AnimationConfigWithData, 'autoplay' | 'loop'>;

const Lottie = ({ animationData, className, onLoad, style, testId, autoplay = false, loop = false }: LottieProps): JSX.Element => {
  const container = useRef<HTMLDivElement>(null);
  const [lottieAnimation, setLottieAnimation] = useState<AnimationItem>();

  useEffect(() => {
    if (container.current) {
      if (lottieAnimation) {
        // Destroy the old animation in the case that new data is passed in
        lottieWeb.destroy();
      }
      const animation = lottieWeb.loadAnimation({
        container: container.current,
        animationData: JSON.parse(animationData),
        loop,
        autoplay
      });
      setLottieAnimation(animation);
      if (onLoad) {
        onLoad(createLottieController(animation));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationData, container]);

  useEffect(
    () => () => {
      if (lottieAnimation) {
        lottieAnimation.destroy();
      }
    },
    [lottieAnimation]
  );

  return <div ref={container} className={className} style={style} data-testid={testId} />;
};

export default Lottie;
